/**
 * Variant Selector
 * Handles variant selection and updates form values and URL
 * Updates add-to-cart button status and text
 * Updates price display
 */

class VariantSelector {
  constructor(container = document) {
    this.container = container;

    // Cache DOM elements scoped to this container
    this.selectors = this.container.querySelectorAll('.variant-selector');
    this.variantIdInput = this.container.querySelector('input[name="id"].product-variant-id');
    this.productForm = this.container.querySelector('.product-form');

    // Find the add to cart button within this container's form
    if (this.productForm) {
      this.addToCartButton = this.productForm.querySelector('button[type="submit"]');
      this.priceContainer = this.productForm.closest('.container')?.querySelector('.price') ||
                            this.container.querySelector('.price');
    }

    // Cache price display elements
    if (this.priceContainer) {
      this.regularPrice = this.priceContainer.querySelector('.price-item--regular');
      this.salePrice = this.priceContainer.querySelector('.price-item--sale');
      this.comparePrice = this.priceContainer.querySelector('.price-item--compare');
    }

    this.productJson = null;
    this.currentVariant = null;

    // Only initialize if we have the necessary elements
    if (this.selectors.length && this.variantIdInput) {
      this.init();
    }
  }

  // Initialize the variant selector
  init() {
    const jsonScript = this.container.querySelector('[data-product-json]');
    if (!jsonScript) {
      console.error('VariantSelector: No product JSON found in container!');
      return;
    }

    try {
      // Parse product JSON
      this.productJson = JSON.parse(jsonScript.textContent);
      if (!this.productJson?.variants?.length) return;

      // Set up event listeners
      this.setupEventListeners();

      // Set initial state
      this.initialVariantId = this.getInitialVariantId();
      this.setInitialState();
    } catch (e) {
      console.error('Error initializing variant selector:', e);
    }
  }

  // Set up all event listeners
  setupEventListeners() {
    // Variant selectors
    this.selectors.forEach(selector =>
      selector.addEventListener('change', this.handleVariantChange.bind(this))
    );
  }

  // Get the variant ID from the URL or from the hidden input
  getInitialVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant') || this.variantIdInput?.value || null;
  }

  // Set the initial variant state
  setInitialState() {
    const variant = this.initialVariantId ?
      this.findVariantById(this.initialVariantId) :
      this.findVariantFromOptions(this.getSelectedOptions());

    if (variant) {
      this.currentVariant = variant;
      this.variantIdInput.value = variant.id;
      this.updateUI(variant);
    }
  }

  // Find a variant by its ID
  findVariantById(variantId) {
    if (!this.productJson?.variants) return null;
    return this.productJson.variants.find(variant =>
      variant.id.toString() === variantId.toString()
    );
  }

  // Handle variant change after user interaction
  handleVariantChange() {
    const selectedOptions = this.getSelectedOptions();
    const selectedVariant = this.findVariantFromOptions(selectedOptions);

    if (selectedVariant) {
      this.currentVariant = selectedVariant;
      this.variantIdInput.value = selectedVariant.id;
      this.updateUrl(selectedVariant.id);
      this.updateUI(selectedVariant);
    }
  }

  // Update all UI elements based on selected variant
  updateUI(variant) {
    this.updateButtonState(variant);
    this.updatePriceDisplay(variant);
    this.updateSwatchDisplay();
  }

  // Update swatch selected value display
  updateSwatchDisplay() {
    // Find all swatch fieldsets in this container and update their selected value display
    this.container.querySelectorAll('.variant-swatches__fieldset').forEach(fieldset => {
      const selectedValueSpan = fieldset.querySelector('.variant-swatches__selected-value');
      const checkedInput = fieldset.querySelector('.swatch-input:checked');

      if (selectedValueSpan && checkedInput) {
        selectedValueSpan.textContent = checkedInput.value;
      }
    });
  }

  // Update URL with variant ID
  updateUrl(variantId) {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
    }
  }

  // Get the currently selected options
  getSelectedOptions() {
    const options = [];
    this.selectors.forEach(selector => {
      if (selector.tagName === 'SELECT') {
        options.push(selector.value);
      } else if (selector.tagName === 'INPUT' && selector.type === 'radio' && selector.checked) {
        options.push(selector.value);
      }
    });
    return options;
  }

  // Find a variant from selected options
  findVariantFromOptions(selectedOptions) {
    if (!this.productJson?.variants) return null;
    return this.productJson.variants.find(variant =>
      selectedOptions.every((option, index) => variant.options[index] === option)
    );
  }

  // Update add-to-cart button state
  updateButtonState(variant) {
    if (!this.addToCartButton) return;

    const buttonText = this.addToCartButton.querySelector('span');
    const isAvailable = variant && variant.available;

    this.addToCartButton.disabled = !isAvailable;

    if (buttonText) {
      buttonText.textContent = !variant ? 'Unavailable' :
                              !isAvailable ? 'Sold Out' : 'Add to Cart';
    }
  }

  // Update price display
  updatePriceDisplay(variant) {
    if (!this.priceContainer || !variant) return;

    const isOnSale = variant.compare_at_price > variant.price;
    this.priceContainer.classList.toggle('price--on-sale', isOnSale);

    if (isOnSale) {
      if (this.salePrice) this.salePrice.innerHTML = this.formatMoney(variant.price);
      if (this.comparePrice) this.comparePrice.innerHTML = this.formatMoney(variant.compare_at_price);
      if (this.regularPrice) this.regularPrice.style.display = 'none';
    } else {
      if (this.regularPrice) {
        this.regularPrice.innerHTML = this.formatMoney(variant.price);
        this.regularPrice.style.display = '';
      }
      if (this.salePrice) this.salePrice.style.display = 'none';
      if (this.comparePrice) this.comparePrice.style.display = 'none';
    }

    this.priceContainer.classList.remove('price--sold-out');
  }

  // Format money for display
  formatMoney(cents) {
    return typeof Shopify !== 'undefined' && Shopify.formatMoney
      ? Shopify.formatMoney(cents)
      : '$' + (cents / 100).toFixed(2);
  }
}

// Initialize when DOM is ready - create an instance for each product form
document.addEventListener('DOMContentLoaded', () => {
  const productForms = document.querySelectorAll('.product-form');
  if (productForms.length === 0) return;

  productForms.forEach((form) => {
    let container = null;

    // Strategy 1: Look for product__info (main product page)
    container = form.closest('.product__info');

    // Strategy 2: Look for featured-product section
    if (!container) {
      container = form.closest('.featured-product');
    }

    // Strategy 3: Look for product section by ID
    if (!container) {
      const sectionId = form.dataset.sectionId;
      if (sectionId) {
        container = document.getElementById(`MainProduct-${sectionId}`) ||
                    document.querySelector(`[id*="${sectionId}"]`);
      }
    }

    // Strategy 4: Find common parent of form and variant-picker
    if (!container) {
      const variantPicker = form.parentElement?.querySelector('.product-variants');
      if (variantPicker) {
        let parent = form.parentElement;
        while (parent && !parent.contains(variantPicker)) {
          parent = parent.parentElement;
        }
        container = parent;
      }
    }

    // Fallback: use immediate parent
    if (!container) {
      container = form.parentElement;
    }

    if (container) {
      new VariantSelector(container);
    }
  });
});
